import React, { useState, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import { getAllAchievements, uploadAchievement, registerAchievement, updateAchievement } from '../../../services/api';
import { getBadgeUrl } from '../../../utils/badgeUrl';

const CATEGORY_LABELS = { staff: 'Staff', creator: 'Creator', birthday: 'Birthday' };

function CategoryTag({ category }) {
    if (!category) return null;
    const colours = {
        staff: 'bg-blue-100 text-blue-800 border-blue-300',
        creator: 'bg-purple-100 text-purple-800 border-purple-300',
        birthday: 'bg-pink-100 text-pink-800 border-pink-300',
    };
    return (
        <span className={`text-[10px] font-['Jersey_10'] uppercase tracking-wide px-1.5 py-0.5 border rounded ${colours[category] || 'bg-gray-100 text-gray-600 border-gray-300'}`}>
            {CATEGORY_LABELS[category] || category}
        </span>
    );
}

function EditModal({ ach, onSave, onCancel, submitting }) {
    const [name, setName] = useState(ach.name);
    const [description, setDescription] = useState(ach.description);
    const [category, setCategory] = useState(ach.category || '');
    const [birthdayDate, setBirthdayDate] = useState(ach.birthdayDate || '');
    const [birthdayUser, setBirthdayUser] = useState(ach.birthdayUser || '');

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/60" onClick={onCancel} />
            <div className="relative bg-offwhite border border-black shadow-xl rounded p-5 w-full max-w-sm mx-4">
                <div className="flex items-center gap-3 mb-4">
                    <div className="bg-white border border-black/20 p-2 flex items-center justify-center shrink-0">
                        <img
                            src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                            alt={ach.badgeID}
                            style={{ imageRendering: 'pixelated', maxWidth: 44, maxHeight: 44 }}
                        />
                    </div>
                    <div className="font-['Jersey_10'] text-xl leading-none">{ach.badgeID}</div>
                </div>

                <div className="space-y-3">
                    <div>
                        <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Name</label>
                        <input
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={100}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                            disabled={submitting}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Description</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            maxLength={255}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                            disabled={submitting}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Team</label>
                        <select
                            value={category}
                            onChange={(e) => setCategory(e.target.value)}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm bg-white"
                            disabled={submitting}
                        >
                            <option value="">None (general)</option>
                            <option value="staff">Staff</option>
                            <option value="creator">Creator</option>
                            <option value="birthday">Birthday</option>
                        </select>
                    </div>
                    {category === 'birthday' && (
                        <>
                            <div>
                                <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Birthday Date (DD-MM)</label>
                                <input
                                    type="text"
                                    value={birthdayDate}
                                    onChange={(e) => setBirthdayDate(e.target.value)}
                                    placeholder="e.g. 22-03"
                                    maxLength={5}
                                    pattern="\d{2}-\d{2}"
                                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Birthday Username</label>
                                <input
                                    type="text"
                                    value={birthdayUser}
                                    onChange={(e) => setBirthdayUser(e.target.value)}
                                    placeholder="Habbo username (no @)"
                                    maxLength={50}
                                    className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                                    disabled={submitting}
                                />
                            </div>
                        </>
                    )}
                </div>

                <div className="flex gap-2 mt-4">
                    <button
                        type="button"
                        onClick={() => onSave({ name: name.trim(), description: description.trim(), category: category || null, birthdayDate: category === 'birthday' ? birthdayDate.trim() || null : null, birthdayUser: category === 'birthday' ? birthdayUser.trim() || null : null })}
                        disabled={submitting || !name.trim()}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        {submitting ? 'Saving...' : 'Save'}
                    </button>
                    <button
                        type="button"
                        onClick={onCancel}
                        disabled={submitting}
                        className="flex-1 font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-gray-300 text-gray-800 hover:bg-gray-400 transition-colors rounded disabled:opacity-50"
                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

function ManageAchievements({ userData }) {
    // Upload state
    const [uploadMode, setUploadMode] = useState('upload'); // 'upload' | 'existing'
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [badgeID, setBadgeID] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [birthdayDate, setBirthdayDate] = useState('');
    const [birthdayUser, setBirthdayUser] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploaded, setUploaded] = useState(null);
    const fileInputRef = useRef(null);

    // Existing badge state
    const [manifest, setManifest] = useState(null);
    const [manifestLoading, setManifestLoading] = useState(false);
    const [selectedExisting, setSelectedExisting] = useState('');

    // Manage state
    const [achievements, setAchievements] = useState([]);
    const [achLoading, setAchLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [editTarget, setEditTarget] = useState(null);
    const [editSubmitting, setEditSubmitting] = useState(false);

    useEffect(() => {
        getAllAchievements()
            .then(data => { if (data?.success) setAchievements(data.achievements || []); })
            .catch(() => toast.error('Failed to load achievements'))
            .finally(() => setAchLoading(false));
    }, []);

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    // ── Upload handlers ─────────────────────────────────────────────────────────

    const deriveBadgeID = (filename) =>
        filename.replace(/\.png$/i, '').toUpperCase().replace(/[^A-Z0-9]/g, '');

    const validateAndSetFile = (selected) => {
        if (!selected) return;
        if (selected.type !== 'image/png') { toast.error('Only PNG files are allowed'); return; }

        const derived = deriveBadgeID(selected.name);
        if (!derived || derived.length < 1 || derived.length > 10) {
            toast.error('Filename must produce a 1 to 10 character alphanumeric badge ID');
            return;
        }

        const objectUrl = URL.createObjectURL(selected);
        const img = new Image();
        img.onload = () => {
            if (img.naturalWidth > 55 || img.naturalHeight > 55) {
                toast.error(`Image must be max 55×55 pixels (got ${img.naturalWidth}×${img.naturalHeight})`);
                URL.revokeObjectURL(objectUrl);
                return;
            }
            setFile(selected);
            setPreview(objectUrl);
            setBadgeID(derived);
            setUploaded(null);
        };
        img.onerror = () => { toast.error('Could not read image'); URL.revokeObjectURL(objectUrl); };
        img.src = objectUrl;
    };

    const handleReset = () => {
        setFile(null); setPreview(null); setBadgeID('');
        setName(''); setDescription(''); setCategory(''); setBirthdayDate(''); setBirthdayUser(''); setUploaded(null);
        setSelectedExisting('');
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const switchMode = (mode) => {
        handleReset();
        setUploadMode(mode);
        if (mode === 'existing' && manifest === null && !manifestLoading) {
            setManifestLoading(true);
            fetch('/img/frankBadges/manifest.json')
                .then(r => r.json())
                .then(data => setManifest(data.badges || []))
                .catch(() => setManifest([]))
                .finally(() => setManifestLoading(false));
        }
    };

    const handleRegisterSubmit = async (e) => {
        e.preventDefault();
        if (!selectedExisting) { toast.error('Please select a badge'); return; }
        if (!name.trim()) { toast.error('Name is required'); return; }
        if (!description.trim()) { toast.error('Description is required'); return; }

        setUploading(true);
        try {
            const bdDate = category === 'birthday' ? birthdayDate.trim() || null : null;
            const bdUser = category === 'birthday' ? birthdayUser.trim() || null : null;
            const data = await registerAchievement(selectedExisting, name.trim(), description.trim(), category || null, bdDate, bdUser);
            if (data?.success) {
                setUploaded(selectedExisting);
                toast.success(`Achievement '${selectedExisting}' registered`);
                setAchievements(prev => [...prev, {
                    badgeID: selectedExisting,
                    name: name.trim(),
                    description: description.trim(),
                    category: category || null,
                    birthdayDate: bdDate,
                    birthdayUser: bdUser,
                    earnedCount: 0,
                    earnedPct: 0,
                }].sort((a, b) => a.badgeID.localeCompare(b.badgeID)));
                // Remove from manifest so it no longer appears as available
                setManifest(prev => prev ? prev.filter(id => id !== selectedExisting) : prev);
            } else {
                toast.error(data?.message || 'Failed to register achievement');
            }
        } catch {
            toast.error('Failed to register achievement');
        } finally {
            setUploading(false);
        }
    };

    const handleUploadSubmit = async (e) => {
        e.preventDefault();
        if (!file) { toast.error('Please select a PNG file'); return; }
        if (!name.trim()) { toast.error('Name is required'); return; }
        if (!description.trim()) { toast.error('Description is required'); return; }

        setUploading(true);
        try {
            const bdDate = category === 'birthday' ? birthdayDate.trim() || null : null;
            const bdUser = category === 'birthday' ? birthdayUser.trim() || null : null;
            const data = await uploadAchievement(file, name.trim(), description.trim(), category || null, bdDate, bdUser);
            if (data?.success) {
                setUploaded(data.badgeID);
                toast.success(`Achievement '${data.badgeID}' uploaded`);
                // Add to the manage list immediately
                setAchievements(prev => [...prev, {
                    badgeID: data.badgeID,
                    name: name.trim(),
                    description: description.trim(),
                    category: category || null,
                    birthdayDate: bdDate,
                    birthdayUser: bdUser,
                    uploaded: true,
                    earnedCount: 0,
                    earnedPct: 0,
                }].sort((a, b) => a.badgeID.localeCompare(b.badgeID)));
            } else {
                toast.error(data?.message || 'Upload failed');
            }
        } catch {
            toast.error('Upload failed');
        } finally {
            setUploading(false);
        }
    };

    // ── Edit handlers ────────────────────────────────────────────────────────────

    const handleEditSave = async (updates) => {
        setEditSubmitting(true);
        try {
            const data = await updateAchievement(editTarget.badgeID, updates);
            if (data?.success) {
                setAchievements(prev => prev.map(a =>
                    a.badgeID === editTarget.badgeID ? { ...a, ...updates } : a
                ));
                toast.success(`'${editTarget.badgeID}' updated`);
                setEditTarget(null);
            } else {
                toast.error(data?.message || 'Update failed');
            }
        } catch {
            toast.error('Update failed');
        } finally {
            setEditSubmitting(false);
        }
    };

    const filteredAchievements = filter.trim()
        ? achievements.filter(a =>
            a.badgeID.toLowerCase().includes(filter.toLowerCase()) ||
            a.name.toLowerCase().includes(filter.toLowerCase())
        )
        : achievements;

    return (
        <AdminLayout userData={userData}>
            <style>{`.habbo-scroll::-webkit-scrollbar{width:16px;height:16px}.habbo-scroll::-webkit-scrollbar-track{background:#cfcfcf;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb{background:#8f8f8f;border:1px solid #000}.habbo-scroll::-webkit-scrollbar-thumb:hover{background:#7a7a7a}`}</style>

            {/* ── Box 1: Upload ── */}
            <ContentBox theme="default" tint="red" header="Upload Achievement">
                {/* Mode toggle */}
                <div className="flex gap-2 mb-4">
                    {[['upload', 'Upload New Image'], ['existing', 'Use Existing Badge']].map(([mode, label]) => (
                        <button
                            key={mode}
                            type="button"
                            onClick={() => switchMode(mode)}
                            className={`flex-1 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-3 py-2 rounded transition-all ${uploadMode === mode ? 'bg-maroon text-white' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <form onSubmit={uploadMode === 'upload' ? handleUploadSubmit : handleRegisterSubmit} className="space-y-4">
                    {uploadMode === 'upload' && (!file ? (
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            onDrop={(e) => { e.preventDefault(); validateAndSetFile(e.dataTransfer.files[0]); }}
                            onDragOver={(e) => e.preventDefault()}
                            className="w-full border-2 border-dashed border-gray-400 rounded bg-gray-50 hover:bg-gray-100 transition-colors py-10 flex flex-col items-center gap-3"
                        >
                            <svg className="w-10 h-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
                            </svg>
                            <span className="font-['Jersey_10'] uppercase text-[14px] tracking-[1px] text-gray-600">Click or drag and drop to upload</span>
                            <span className="text-xs text-gray-400">PNG only, max 55x55 pixels</span>
                        </button>
                    ) : (
                        <div className="flex items-center gap-4 border-2 border-gray-300 rounded bg-gray-50 p-3">
                            <div className="shrink-0 bg-white border border-black/20 p-2 flex items-center justify-center">
                                <img src={preview} alt="Badge preview" style={{ imageRendering: 'pixelated', maxWidth: 55, maxHeight: 55 }} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-['Jersey_10'] text-coal truncate">{file.name}</div>
                                <div className="text-xs text-coal/60 mt-0.5">Badge ID: <span className="font-bold text-coal">{badgeID}</span></div>
                            </div>
                            <button type="button" onClick={handleReset} className="shrink-0 text-xs text-gray-500 hover:text-red-600 font-['Jersey_10'] uppercase tracking-wide transition-colors">
                                Remove
                            </button>
                        </div>
                    ))}

                    <input ref={fileInputRef} type="file" accept="image/png" onChange={(e) => validateAndSetFile(e.target.files[0])} className="hidden" />

                    {uploadMode === 'existing' && (() => {
                        const usedIDs = new Set(achievements.map(a => a.badgeID));
                        const available = (manifest || []).filter(id => !usedIDs.has(id));
                        return (
                            <div className="space-y-3">
                                {manifestLoading ? (
                                    <div className="text-sm text-coal/70">Loading available badges...</div>
                                ) : (
                                    <div>
                                        <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Select Badge</label>
                                        <select
                                            value={selectedExisting}
                                            onChange={(e) => setSelectedExisting(e.target.value)}
                                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm bg-white"
                                            disabled={uploading}
                                        >
                                            <option value="">Choose a badge</option>
                                            {available.map(id => (
                                                <option key={id} value={id}>{id}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {selectedExisting && (
                                    <div className="flex items-center gap-3 border-2 border-gray-300 rounded bg-gray-50 p-3">
                                        <div className="shrink-0 bg-white border border-black/20 p-2 flex items-center justify-center">
                                            <img
                                                src={`/img/frankBadges/${selectedExisting}.png`}
                                                alt={selectedExisting}
                                                style={{ imageRendering: 'pixelated', maxWidth: 55, maxHeight: 55 }}
                                            />
                                        </div>
                                        <div className="font-['Jersey_10'] text-lg text-coal">{selectedExisting}</div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Name</label>
                            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Achievement name" maxLength={100} className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm" disabled={uploading} />
                        </div>
                        <div>
                            <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Team</label>
                            <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm bg-white" disabled={uploading}>
                                <option value="">None (general)</option>
                                <option value="staff">Staff</option>
                                <option value="creator">Creator</option>
                                <option value="birthday">Birthday</option>
                            </select>
                        </div>
                    </div>
                    {category === 'birthday' && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Birthday Date (DD-MM)</label>
                                <input type="text" value={birthdayDate} onChange={(e) => setBirthdayDate(e.target.value)} placeholder="e.g. 22-03" maxLength={5} className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm" disabled={uploading} />
                            </div>
                            <div>
                                <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Birthday Username</label>
                                <input type="text" value={birthdayUser} onChange={(e) => setBirthdayUser(e.target.value)} placeholder="Habbo username (no @)" maxLength={50} className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm" disabled={uploading} />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className="block text-xs font-['Jersey_10'] uppercase tracking-wide text-coal/70 mb-1">Description</label>
                        <input type="text" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Achievement description" maxLength={255} className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm" disabled={uploading} />
                    </div>

                    {uploaded && (
                        <div className="bg-green-50 border border-green-300 rounded p-3">
                            <p className="text-xs text-green-700 font-['Jersey_10'] uppercase tracking-wide mb-2">Achievement '{uploaded}' uploaded successfully</p>
                            <div className="flex items-center gap-3">
                                <img src={getBadgeUrl(uploaded, uploadMode === 'upload')} alt={uploaded} style={{ imageRendering: 'pixelated', maxWidth: 44, maxHeight: 44 }} />
                                <div>
                                    <div className="font-['Jersey_10'] text-lg leading-none">{name}</div>
                                    <div className="text-xs text-coal/70">{description}</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {!uploaded ? (
                        <button
                            type="submit"
                            disabled={uploading || !name.trim() || !description.trim() || (uploadMode === 'upload' ? !file : !selectedExisting)}
                            className="w-full font-['Jersey_10'] uppercase text-[16px] tracking-[1px] px-4 py-2 bg-maroon text-white hover:bg-red-900 transition-colors rounded disabled:opacity-50"
                            style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                        >
                            {uploading ? 'Saving...' : uploadMode === 'upload' ? 'Upload Achievement' : 'Register Achievement'}
                        </button>
                    ) : (
                        <button type="button" onClick={handleReset} className="w-full font-['Jersey_10'] uppercase text-[14px] tracking-[1px] px-4 py-2 bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors rounded" style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}>
                            Upload Another
                        </button>
                    )}
                </form>
            </ContentBox>

            {/* ── Box 2: Modify existing ── */}
            <div className="mt-4">
                <ContentBox theme="default" tint="red" header="Modify Achievements" padding="p-0">
                    <div className="p-3 border-b border-black/10">
                        <input
                            type="text"
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            placeholder="Filter by ID or name..."
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded focus:outline-none focus:border-maroon text-sm"
                        />
                    </div>
                    {achLoading ? (
                        <div className="p-3 text-coal/70 text-sm">Loading...</div>
                    ) : filteredAchievements.length === 0 ? (
                        <div className="p-3 text-coal/70 text-sm">{filter ? 'No achievements match your filter.' : 'No achievements found.'}</div>
                    ) : (
                        <div className="habbo-scroll overflow-y-auto flex flex-col" style={{ maxHeight: 400 }}>
                            {filteredAchievements.map((ach, idx) => (
                                <div key={ach.badgeID} className={`flex items-center gap-3 px-3 py-2 border-b border-black/10 ${idx % 2 === 0 ? 'bg-black/5' : 'bg-black/10'}`}>
                                    <div className="shrink-0 w-10 flex items-center justify-center">
                                        <img
                                            src={getBadgeUrl(ach.badgeID, ach.uploaded)}
                                            alt={ach.badgeID}
                                            style={{ imageRendering: 'pixelated', maxWidth: 36, maxHeight: 36 }}
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-['Jersey_10'] text-lg leading-none text-coal">{ach.name}</span>
                                            <CategoryTag category={ach.category} />
                                        </div>
                                        <div className="text-xs text-coal/60 truncate">
                                            {ach.badgeID}: {ach.description}
                                            {ach.category === 'birthday' && ach.birthdayUser && (
                                                <span className="ml-1 text-pink-600">({ach.birthdayDate} · {ach.birthdayUser})</span>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setEditTarget(ach)}
                                        className="shrink-0 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] px-3 py-1 bg-gray-200 text-gray-800 hover:bg-gray-300 transition-colors rounded"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.2)' }}
                                    >
                                        Edit
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </ContentBox>
            </div>

            {editTarget && (
                <EditModal
                    ach={editTarget}
                    onSave={handleEditSave}
                    onCancel={() => setEditTarget(null)}
                    submitting={editSubmitting}
                />
            )}
        </AdminLayout>
    );
}

export default ManageAchievements;
